import { DELETE, GET, PATCH, POST, PUT } from "@/app/services/apiService";
import { ReqBody } from "@/app/services/modelService";
import { saveDataToSessionStorage } from "@/app/services/storageService";
import { saveMixpanelUserInfo } from "@/app/services/mixpanelService";

export async function getMasterConfig() {
  const configParams = {
    url: `configuration`,
    params: [{ code: ["help-assets"] }],
  };
  const response = await GET(configParams);
  return response;
}

export const signIn = async (
  credential: any,
  headers = { "x-user-type": "provider" }
) => {
  if (credential.email.length > 0) {
    const req: ReqBody = {
      url: "accounts/signin",
      data: { email: credential.email, password: credential.password },
      headers,
    };
    const res: any = await POST(req);
    if (res && res.data) {
      if (res.data.accessToken) {
        saveDataToSessionStorage("access-token", res.data.accessToken);
      }
      if (res.data.refreshToken) {
        saveDataToSessionStorage("refresh-token", res.data.refreshToken);
      }
    }
    return res;
  }
  return null;
};

export async function verifyOTP(
  credentials: any,
  headers = { "x-user-type": "provider" }
) {
  const { credential = {}, otp = "", session = "" } = credentials;
  const postConfig = {
    data: { ...credential, otp, session },
    url: "accounts/verifyOtp",
    headers,
  };
  const response = await POST(postConfig);
  if (response && response.data && !response.data.challengeName) {
    if (response.data.accessToken) {
      saveDataToSessionStorage("access-token", response.data.accessToken);
    }
    if (response.data.refreshToken) {
      saveDataToSessionStorage("refresh-token", response.data.refreshToken);
    }
  }
  return response;
}

export async function getProfile() {
  const profileConfig = { url: "users" };
  const response = await GET(profileConfig);
  if (response) saveMixpanelUserInfo(response.data);
  return response;
}

export async function userSignout({ data = {} }) {
  const config = {
    url: "accounts/signout",
    data,
    headers: { "x-platform": "web" },
  };
  const response = await POST(config);
  return response;
}

export function updateUserProfile(data = {}) {
  const postConfig = { data: data, url: `users` };
  const response = PATCH(postConfig);
  return response;
}

export async function fileUpload(image: any = {}) {
  const { type = "" } = image;
  if (type === "") return "";
  const config = {
    filename: new Date().toString(),
    contentType: type,
  };

  const awsConfig = { url: "users/avatar", data: config };
  const { data = {} } = await POST(awsConfig);
  const { fields } = data;
  const { key } = fields;
  const formData = new FormData();

  Object.keys(fields).forEach((key) => {
    formData.append(key, fields[key]);
  });

  formData.append("file", image);
  await POST({
    url: data.url,
    data: formData,
    appConfig: { baseURL: "", doNotNeedAuthorizationHeader: true },
    headers: { "Content-Type": "Multipart/form-data" },
    doNotNeedAuthorizationHeader: true,
  });

  return key;
}

export async function getDoctorList(params: any = {}) {
  const patientConfig = {
    url: `doctors`,
    params: params,
  };
  const response = await GET(patientConfig);
  return response;
}

export async function getNotificationList(params: any = {}, options: any = {}) {
  const patientConfig: any = {
    url: `notifications`,
    params: params,
  };
  if (options && options.signal) patientConfig.signal = options.signal;
  const response = await GET(patientConfig);
  return response;
}

export async function getFeatureList() {
  const patientConfig = {
    url: `feature`,
  };
  const response = await GET(patientConfig);
  return response;
}

export async function resetNotificationCount(params: any = {}) {
  const patientConfig = {
    url: `notifications`,
    params: params,
  };
  const response = await GET(patientConfig);
  return response;
}

export async function requestOTP(
  credentials: any,
  headers = { "x-user-type": "provider" }
) {
  const postConfig = { data: credentials, url: "accounts/requestOtp", headers };
  const response = await POST(postConfig);
  return response;
}

export async function sendOTP(data: any) {
  const config = { data: data, url: `/accounts` };
  const response = await PATCH(config);
  return response;
}

export async function verifyUpdateOtp(data: any) {
  const config = { data: data, url: `/accounts/verify` };
  const response = await PATCH(config);
  return response;
}

export async function requestResetLinkToEmail(
  credentials: any,
  headers = { "x-user-type": "provider" }
) {
  const postConfig = {
    data: credentials,
    url: "accounts/forgot-password",
    headers,
  };
  const response = await POST(postConfig);
  return response;
}

export async function requestToChangePassword(
  credentials: any,
  headers = { "x-user-type": "provider" }
) {
  const postConfig = {
    data: credentials,
    url: "accounts/change-password",
    headers,
  };
  const response = await POST(postConfig);
  return response;
}

export async function requestToResetPassword(
  credentials: any,
  headers = { "x-user-type": "provider" }
) {
  const postConfig = {
    data: credentials,
    url: "accounts/reset-password",
    headers,
  };
  const response = await POST(postConfig);
  return response;
}

export async function getDietPlans(params: any = {}) {
  const patientConfig = {
    url: `diets`,
    params: params,
  };
  const response = await GET(patientConfig);
  return response;
}

export async function getADietPlan(planId: any = {}) {
  const patientConfig = {
    url: `diets/${planId}`,
  };
  const response = await GET(patientConfig);
  return response;
}

export async function updateMealPlans(planId: number | null, data: any = {}) {
  if (data.repast && data.repast.length === 0) delete data.repast;
  const patientConfig = {
    url: planId ? `diets/${planId}` : "diets",
    data,
  };
  const response = planId
    ? await PUT(patientConfig)
    : await POST(patientConfig);
  return response;
}

export async function cloneDietPlan(planId: any = {}) {
  const patientConfig = {
    url: "diets/clone",
    data: { dietPlanId: planId },
  };
  const response = await POST(patientConfig);
  return response;
}

export async function deleteDietPlans(planId: number) {
  const patientConfig = {
    url: `diets/${planId}`,
  };
  const response = await DELETE(patientConfig);
  return response;
}

export async function shareDietPlans(data: any = {}) {
  const patientConfig = {
    url: `/diets/share`,
    data: data,
  };
  const response = await POST(patientConfig);
  return response;
}

export async function deleteMealPlans(dietPlanId: number, mealPlanId: number) {
  const patientConfig = {
    url: `diets/${dietPlanId}/details/${mealPlanId}`,
  };
  const response = await DELETE(patientConfig);
  return response;
}
